<template>
	<teleport to="body">
		<transition name="modal" appear>
			<div v-if="isOpen" class="base-modal" @click.self="handleBackdropClick">
				<div class="base-modal__dialog">
					<div v-if="$slots.header" class="base-modal__header">
						<slot name="header" />
						<button class="base-modal__close" type="button" @click="$emit('close')">×</button>
					</div>

					<div v-if="$slots.body" class="base-modal__body">
						<slot name="body" />
					</div>

					<div v-if="$slots.footer" class="base-modal__footer">
						<slot name="footer" />
					</div>
				</div>
			</div>
		</transition>
	</teleport>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted } from 'vue';

export default defineComponent({
	name: 'BaseModal',

	props: {
		isOpen: {
			type: Boolean,
			default: false,
		},
		closeOnBackdrop: {
			type: Boolean,
			default: true,
		},
	},

	emits: ['close'],

	setup(props, { emit }) {
		const handleBackdropClick = () => {
			if (props.closeOnBackdrop) {
				emit('close');
			}
		};

		const handleEscapeKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				emit('close');
			}
		};

		onMounted(() => {
			document.addEventListener('keydown', handleEscapeKey);
			if (props.isOpen) {
				document.body.style.overflow = 'hidden';
			}
		});

		onUnmounted(() => {
			document.removeEventListener('keydown', handleEscapeKey);
			document.body.style.overflow = '';
		});

		return {
			handleBackdropClick,
		};
	},
});
</script>

<style scoped lang="scss">
.base-modal {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	position: fixed;
	top: 0;
	left: 0;
	z-index: 1000;
	background: rgba(0, 0, 0, 0.5);

	&__dialog {
		display: flex;
		flex-direction: column;
		min-width: 400px;
		max-width: 90vw;
		max-height: 90vh;
		overflow: hidden;
		border-radius: 8px;
		background: white;
		box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
	}

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 1.5rem;
		border-bottom: 1px solid #e5e7eb;

		h3 {
			margin: 0;
			font-size: 1.125rem;
			font-weight: 600;
		}
	}

	&__close {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 24px;
		height: 24px;
		padding: 0;
		color: #6b7280;
		font-size: 1.5rem;
		cursor: pointer;
		border: none;
		background: none;

		&:hover {
			color: #374151;
		}
	}

	&__body {
		flex: 1;
		padding: 1.5rem;
		overflow-y: auto;
	}

	&__footer {
		display: flex;
		justify-content: flex-end;
		padding: 1.5rem;
		border-top: 1px solid #e5e7eb;
		gap: 0.75rem;
	}
}

.modal-enter-active,
.modal-leave-active {
	transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
	opacity: 0;
}

.modal-enter-active .base-modal__dialog,
.modal-leave-active .base-modal__dialog {
	transition: transform 0.3s ease;
}

.modal-enter-from .base-modal__dialog,
.modal-leave-to .base-modal__dialog {
	transform: scale(0.9);
}
</style>
